/**
 * Porte d'accès applicative (login principal, contrat v1.38 §8bis — décision
 * utilisateur 2026-09-09).
 *
 * Régime **distinct** de la session de poste (`session.ts`) et de la session
 * d'administration (`adminSession.ts`) : ici la session vit dans un cookie
 * `sentinelle_session` (HttpOnly, donc **invisible au JS**) posé par le backend.
 * Le frontend ne lit ni n'écrit ce cookie ; il se contente d'appeler
 * `GET /auth/me` au démarrage et de réagir à un `401`.
 *
 * Ce module ne porte donc **aucun jeton**. Il expose seulement :
 *  - un petit bus `onUnauthorized` : la couche HTTP le déclenche dès qu'une
 *    route (hors endpoints d'authentification eux-mêmes) répond `401`, ce qui
 *    signifie « le cookie a expiré / a été révoqué » → l'app rebascule sur
 *    l'écran de connexion (brief §4) ;
 *  - `describeAppLoginError`, pour un message d'erreur homogène.
 */

import { ApiError } from "../api/errors";

type UnauthorizedListener = () => void;

const listeners = new Set<UnauthorizedListener>();

/**
 * S'abonne aux `401` « inattendus » (session expirée / révoquée), émis par la
 * couche HTTP. Rend une fonction de désabonnement.
 */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Déclenché par `HttpClient` sur un `401` venu d'une route qui n'est pas un
 * endpoint d'authentification (voir `AUTH_PATHS` dans `api/http.ts`). Ne PAS
 * appeler depuis l'écran de connexion : lui gère ses `401` localement.
 */
export function emitUnauthorized(): void {
  for (const listener of [...listeners]) listener();
}

/**
 * Message affiché sous le formulaire de connexion (brief §3) :
 *  - `401` → mêmes mots quel que soit le cas (compte inconnu / révoqué / mot de
 *    passe faux) : aucun oracle ;
 *  - `422` → corps incomplet ;
 *  - le reste → message transport générique.
 */
export function describeAppLoginError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.httpStatus === 401 || error.code === "unauthenticated") return "Identifiants invalides.";
    if (error.httpStatus === 422 || error.code === "validation_failed") {
      return "Identifiant ou mot de passe manquant.";
    }
    return error.message;
  }
  return "La connexion n'a pas abouti. Réessayez.";
}
